<template>
  <header class="ats-nav" :class="{ 'ats-nav--overlay': overlay }">
    <div class="ats-nav__inner">
      <router-link class="ats-brand" to="/" title="返回官网首页">
        <img
          class="ats-brand__logo"
          :src="brandLogo"
          alt="大救星"
          loading="eager"
          decoding="sync"
        >
      </router-link>
      <nav class="ats-nav__links" aria-label="主导航">
        <template v-for="item in navItems">
          <div
            v-if="item.kind === 'dropdown'"
            :key="`${item.id}-dropdown`"
            class="ats-nav__dropdown"
            :class="{
              'is-open': openDropdownId === item.id,
              'is-active': activeNavId === item.id
            }"
          >
            <button
              type="button"
              class="ats-nav__dropdown-trigger"
              :aria-expanded="openDropdownId === item.id ? 'true' : 'false'"
              aria-haspopup="true"
              @click.stop="toggleDropdown(item.id)"
            >
              {{ item.label }}
            </button>
            <div class="ats-nav__dropdown-menu" role="menu">
              <a
                v-for="child in item.children"
                :key="child.id"
                :href="child.href"
                role="menuitem"
                @click.prevent="onNavClick(child)"
              >{{ child.label }}</a>
            </div>
          </div>
          <a
            v-else
            :key="`${item.id}-link`"
            :href="item.href"
            :class="{ 'is-active': activeNavId === item.id }"
            @mouseenter="onNavHover(item)"
            @click.prevent="onNavClick(item)"
          >{{ item.label }}</a>
        </template>
      </nav>
    </div>
  </header>
</template>

<script>
import util from '@/util/util.js'
import { getAtsNavItems, handleAtsNavClick } from '@/util/atsNavConfig'
import { preloadAtsPageByPath } from '@/util/atsImagePreload'

/**
 * 品牌落地页 / 全球救援页 共用顶栏导航
 */
export default {
  name: 'AtsSiteNav',
  props: {
    /** @type {string} 当前高亮导航 id */
    activeNavId: {
      type: String,
      required: true
    },
    /** @type {'savior'|'globalRescue'|'rescueList'|'healthMonitoring'|'firstAidKit'|'companyIntro'} 页面类型，决定链接目标 */
    page: {
      type: String,
      default: 'savior'
    },
    /** @type {boolean} 是否叠在首屏背景上（透明底） */
    overlay: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      /** @type {string} 顶栏品牌 Logo */
      brandLogo: require('@/assets/logo.png'),
      /** @type {string|null} 当前展开的下拉菜单 id */
      openDropdownId: null
    }
  },
  computed: {
    /** @returns {ReturnType<typeof getAtsNavItems>} */
    navItems () {
      return getAtsNavItems(util, this.page)
    }
  },
  mounted () {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    /** @returns {void} */
    closeDropdown () {
      this.openDropdownId = null
    },
    /**
     * @param {string} id
     * @returns {void}
     */
    toggleDropdown (id) {
      this.openDropdownId = this.openDropdownId === id ? null : id
    },
    /**
     * @param {MouseEvent} event
     * @returns {void}
     */
    onDocumentClick (event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown()
      }
    },
    /**
     * 悬停时预加载目标页首屏图，减少点击后闪屏
     * @param {ReturnType<typeof getAtsNavItems>[number]} item
     * @returns {void}
     */
    onNavHover (item) {
      if ((item.kind === 'route' || item.kind === 'top') && item.target) {
        preloadAtsPageByPath(item.target)
      }
    },
    /**
     * @param {ReturnType<typeof getAtsNavItems>[number]} item
     * @returns {void}
     */
    onNavClick (item) {
      this.closeDropdown()
      this.$emit('nav-click', item)
      handleAtsNavClick(this.$router, item)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './mixins';

.ats-nav {
  border-bottom: 1px solid #ffffff;
  box-sizing: border-box;
}

.ats-nav--overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  height: 100px;
  display: flex;
  align-items: center;
  background: transparent;
  box-shadow: none;
}

.ats-nav__inner {
  width: 100%;
  max-width: 1320px;
  height: 100%;
  margin: 0 auto;
  padding: 0 36px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.ats-brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  text-decoration: none;
  line-height: 0;
  @include ats-hover-icon;

  &:hover {
    opacity: 0.92;
  }
}

.ats-brand__logo {
  display: block;
  height: 40px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
  object-position: left center;
}

.ats-brand.router-link-active .ats-brand__logo {
  opacity: 1;
}

.ats-nav__links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px 28px;

  a,
  .ats-nav__dropdown-trigger {
    position: relative;
    color: rgba(255, 255, 255, 0.92);
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    white-space: nowrap;
    padding-bottom: 8px;
    border: none;
    @include ats-interactive-base;
    background: transparent;
    cursor: pointer;
    font-family: inherit;
    line-height: inherit;

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      width: 0;
      height: 4px;
      background: transparent;
      border-radius: 2px;
      transform: translateX(-50%);
      transition: width 0.18s ease, background-color 0.18s ease;
    }
  }

  a:hover,
  .ats-nav__dropdown-trigger:hover,
  .ats-nav__dropdown.is-open .ats-nav__dropdown-trigger {
    color: #fff;
    transform: translateY(-2px);
  }

  a.is-active,
  .ats-nav__dropdown.is-active .ats-nav__dropdown-trigger {
    color: #fff;
    font-weight: 600;
    transform: translateY(-2px);

    &::after {
      width: 30px;
      background: #ffffff;
    }
  }
}

.ats-nav__dropdown {
  position: relative;
}

.ats-nav__dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 148px;
  padding: 8px 0 6px;
  background: rgba(20, 36, 32, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  box-sizing: border-box;
  z-index: 50;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
  }

  a {
    display: block;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.92);
    border-bottom: none;
    white-space: nowrap;
    @include ats-interactive-base;

    &:hover {
      background: rgba(143, 233, 201, 0.12);
      color: #fff;
      transform: translateX(3px);
    }
  }
}

.ats-nav__dropdown:hover .ats-nav__dropdown-menu,
.ats-nav__dropdown.is-open .ats-nav__dropdown-menu {
  display: block;
}

@media (max-width: 960px) {
  .ats-nav--overlay {
    height: auto;
    min-height: 72px;
    align-items: flex-start;
    padding: 12px 0;
  }
  .ats-nav__inner {
    flex-direction: column;
    align-items: stretch;
    padding: 0 20px;
    height: auto;
  }
  .ats-nav__links {
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 6px 14px;
    width: 100%;

    a,
    .ats-nav__dropdown-trigger {
      font-size: 13px;
      white-space: normal;
      padding-bottom: 6px;
    }
  }
  .ats-nav__dropdown-menu {
    right: auto;
    left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ats/AtsSiteNav.vue